import styled from "@emotion/styled"
import { Box, Button, Dialog, Typography } from "@mui/material"
import * as api from "api"
import CloseButton from "common/CloseButton"
import RatioElement from "common/ElementRatio"
import ResponsiveModelImage from "common/ResponsiveModelImage"
import ContactUsButton from "components/ContactUs/ContactUsButton"
import VehicleFinishingLevelCategories from "components/Vehicle/VehicleFinishingLevelCategories"
import VehicleFreeOptions from "components/Vehicle/VehicleFreeOptions"
import VehicleMissons from "components/Vehicle/VehicleMissons"
import VehicleNotices from "components/Vehicle/VehicleNotices"
import VehiclePrice from "components/Vehicle/VehiclePrice"
import { memo } from "react"
import Shared from "theme/shared"

const Styled = {
  Wrapper: styled.div`
    overflow: hidden;
    display: flex;
    flex-direction: column;
    margin: 20px auto;
    width: 100%;
    text-align: center;

    @media (max-width: 500px) {
      .price-comment {
        display: block;
      }
    }
  `,
  Main: styled.div`
    overflow-y: auto;
    flex: 1;
  `,
  Pad: styled.div`
    padding: 0 14px;
  `,
  PriceAndNotices: styled.div`
    padding: 0 14px;
  `,
  Grid: styled.div`
    text-align: right;
    display: flex;
    justify-content: space-between;
    & > :not(:first-child) {
      margin-right: 20px;
    }
  `,
  Title: styled(Typography)`
    font-size: 22px;
    font-weight: bold;
    color: ${(p) => p.theme.colors.marine};
  `,
  BodyStyle: styled(Typography)`
    font-size: 13px;
    font-weight: 500;
    color: ${(p) => p.theme.colors.marine};
  `,
  Button: styled(Button)`
    font-size: 14px;
    position: sticky;
    bottom: 0;
  `,
}

type VehicleChildDialogMobileProps = {
  onClose: () => void
  data: api.VehicleChildren | api.VehicleFinishingLevel
  type: api.FamilyTypes
  modelId: number
}

const VehicleChildDialogMobile = ({
  onClose,
  data,
  type,
  modelId,
}: VehicleChildDialogMobileProps) => (
  <Dialog open={data != null} onClose={onClose}>
    {data != null && (
      <Styled.Wrapper>
        <CloseButton onClick={onClose} />
        <Styled.Main>
          <Styled.Pad>
            <Styled.Title>
              {data.name}
              {!api.Types.isFinishingLevel(data, type) &&
                ` | L${data.industryCodeLength}H${data.industryCodeHeight}`}
            </Styled.Title>
            <Styled.BodyStyle>{data.bodyStyle}</Styled.BodyStyle>
            <Shared.SimpleDivider />
            <VehicleMissons data={data.missions} />
            <RatioElement ratio={16 / 9}>
              <ResponsiveModelImage image={data.image} />
            </RatioElement>
            <VehicleFreeOptions data={data.freeOptions} />
          </Styled.Pad>

          {!api.Types.isFinishingLevel(data, type) && <Shared.SimpleDivider />}
          {api.Types.isFinishingLevel(data, type) && (
            <VehicleFinishingLevelCategories data={data.categories} />
          )}

          <Styled.PriceAndNotices>
            <Styled.Grid>
              {data.displayPriceWithoutVat && (
                <VehiclePrice price={data.priceWithoutVAT} vat={false} />
              )}
              <VehiclePrice price={data.priceWithVAT} vat={true} />
            </Styled.Grid>
            <Box textAlign="left">
              <VehicleNotices type="VehicleChild" data={data} />
            </Box>
          </Styled.PriceAndNotices>
        </Styled.Main>
        <Box flexShrink={1}>
          <Styled.Pad>
            <ContactUsButton
              modelId={modelId}
              component={<Styled.Button variant="contained" />}
            />
          </Styled.Pad>
        </Box>
      </Styled.Wrapper>
    )}
  </Dialog>
)

export default memo(VehicleChildDialogMobile)
